import { API_ENDPOINTS, ApiResponse } from 'services/api';
import httpClient from 'services/httpClient';
import { DetailCourse, FormCreateCourseLCMS, IMaterialFormData } from 'types/lcms/courses';

interface DetailApiResponse {
  message?: string;
  data?: { data?: DetailCourse };
}

interface Params {
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  status?: string;
  sortBy?: string;
  startDate?: string;
  endDate?: string;
  courseid?: string | null;
  sortOrder?: 'asc' | 'desc';
  jobPosition?: string;
  courseCategory?: string;
  competencyCategory?: string;
}

interface ParamEmplec {
  search?: string;
  isEmployee?: boolean;
  isLecturer?: boolean;
}

const defaultResponse = {
  pageNumber: 1,
  pageSize: 10,
  totalPages: 0,
  totalRecords: 0,
  message: 'Failed to fetch data',
  data: []
};

const defualtGetParams = {
  pageNumber: 1,
  pageSize: 20000
};

export interface DefaultOptionBinus {
  id: number;
  option: string;
}

export interface DefaultBusinessUnitData {
  buName: string;
  buCode: string;
}

export interface JobFamilyGroupData {
  jobFamilyGroupID: string;
  jobFamilyGroupName: string;
}

export interface JobFamilyData {
  jobFamilyID: string;
  jobFamilyName: string;
}

export interface JobPositionData {
  namaJabatan: string;
}

export interface JobProfileData {
  jobProfileId: string;
  jobProfileName: string;
}

type ListOfDefaultOptionBinus = DefaultOptionBinus[];
type ListOfDefaultData = DefaultBusinessUnitData[];
type ListOfJobFamilyGroupData = JobFamilyGroupData[];
type ListOfJobFamilyData = JobFamilyData[];
type ListOfJobPositionData = JobPositionData[];
type ListOfJobProfileData = JobProfileData[];

export const courseService = {
  list: async (params?: Params): Promise<ApiResponse<any>> => {
    try {
      const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.LIST, {
        params: { ...params, sortOrder: 'desc' }
      });
      return response.data;
    } catch {
      return {
        ...defaultResponse,
        pageNumber: params?.pageNumber || 1,
        pageSize: params?.pageSize || 10
      };
    }
  },
  detail: async (id: string): Promise<DetailApiResponse> => {
    try {
      const response = await httpClient.get(`${API_ENDPOINTS.LCMS.COURSES.DETAIL}/${id}`);
      return response;
    } catch (error: any) {
      return {
        message: error?.message || 'Failed to get detail course',
        data: undefined // Empty object cast as DetailCourse for error case
      };
    }
  },
  detailByLms: async (id: string): Promise<DetailApiResponse> => {
    try {
      const response = await httpClient.get(`${API_ENDPOINTS.LCMS.COURSES.DETAIL_BY_LMS}/${id}`);
      return response;
    } catch (error: any) {
      return {
        message: error?.message || 'Failed to get detail course by LMS',
        data: undefined
      };
    }
  },
  create: async (data: FormCreateCourseLCMS) => {
    const response = await httpClient.post(API_ENDPOINTS.LCMS.COURSES.ADD, data);
    return response.data;
  },
  update: async (data: FormCreateCourseLCMS) => {
    const response = await httpClient.put(API_ENDPOINTS.LCMS.COURSES.EDIT, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await httpClient.delete(`${API_ENDPOINTS.LCMS.COURSES.DELETE}/${id}`);
    return response.data;
  },
  checkInBeelajarLMS: async (id: string) => {
    const response = await httpClient.post(`${API_ENDPOINTS.LCMS.COURSES.CHECK_IN_BEELAJAR}/${id}`);
    return response.data;
  },
  checkOutBeelajarLMS: async (id: string) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.LCMS.COURSES.CHECK_OUT_BEELAJAR}/${id}`
    );
    return response.data;
  },
  getEmployeeLecturerData: async (params?: ParamEmplec) => {
    try {
      const response = await httpClient.get(API_ENDPOINTS.HC.EMPLOYEE_LECTURER_LIST, {
        params
      });
      return response.data;
    } catch {
      return {
        ...defaultResponse
      };
    }
  },
  createReason: async (data: { msLCMSCourseID: string; reason: string; binusianId: string }) => {
    const response = await httpClient.post(API_ENDPOINTS.LCMS.COURSES.ADD_HISTORY, data);
    return response.data;
  },
  getLevelOfTraining: async (params?: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.LEVEL_OF_TRAINING, { params });
    return response.data;
  },
  getBands: async (params?: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.BANDS, { params });
    return response.data;
  },
  getTrainingType: async (params?: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.TRAINING_TYPE, { params });
    return response.data;
  },
  getModeOfDelivery: async (params?: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.MODE_OF_DELIVERY, { params });
    return response.data;
  },
  getLearningFormat: async (params?: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.LEARNING_FORMAT, { params });
    return response.data;
  },
  getPassingFormula: async (params?: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.PASSING_FORMULA, { params });
    return response.data;
  },
  getPICDetail: async (params?: { h: string }) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.PIC_DETAIL, { params });
    return response.data;
  },
  getPICMaterialDetail: async (params?: { materialId: string }) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.PIC_MATERIAL_DETAIL, {
      params
    });
    return response.data;
  },
  getSMESubmitDetail: async (params?: { h: string }) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.SME_SUBMIT_DETAIL, {
      params
    });
    return response.data;
  },
  picSubmitApproval: async (data: {
    hash: string;
    approvals: {
      materialId: string;
      decision: string;
      comments: string;
      urlFix?: string;
      typeFix?: string;
    }[];
  }) => {
    const response = await httpClient.post(API_ENDPOINTS.LCMS.COURSES.PIC_SUMBIT_APPROVAL, data);
    return response.data;
  },
  smeDeleteMaterial: async (data: { hash: string; materialIds: string[] }) => {
    const response = await httpClient.post(API_ENDPOINTS.LCMS.COURSES.SME_DELETE, data);
    return response.data;
  },
  smeSubmitMaterial: async (data: { hash: string; materials: IMaterialFormData[] }) => {
    const response = await httpClient.post(API_ENDPOINTS.LCMS.COURSES.SME_SUBMIT_MATERIAL, data);
    return response.data;
  },
  getSMEFaciliator: async (params: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.FACILITATOR, {
      params
    });
    return response.data;
  },
  getTypeActivity: async (params: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.TYPE_ACTIVITY, {
      params
    });
    return response.data;
  },
  getSectionByCourse: async (params: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.SECTION_BY_COURSE, {
      params
    });
    return response.data;
  },
  getCourseCategories: async (params: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.COURSE_CATEGORIES, {
      params
    });
    return response.data;
  },
  getCompetencyCategories: async (params: Params) => {
    const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.COMPETENCY_CATEGORIES, {
      params
    });
    return response.data;
  },
  getActivityByModules: async (data: { moduleIds: number[] }) => {
    const response = await httpClient.post(API_ENDPOINTS.LCMS.COURSES.COURSE_BY_MODULES, data);
    return response.data;
  },
  adminSubmitMaterial: async (data: { courseId: string; materials: IMaterialFormData[] }) => {
    const response = await httpClient.post(API_ENDPOINTS.LCMS.COURSES.ADMIN_SUBMIT_MATERIAL, data);
    return response.data;
  },
  getLCMSTags: async (params: { pageNumber: number; pageSize: number }) => {
    const response = await httpClient.get<{
      data: { id: number; name: string; rawName: string }[];
    }>(API_ENDPOINTS.LCMS.COURSES.TAGS_LIST, {
      params
    });
    return response.data;
  },
  getBusinessUnitCreateLCMS: async () => {
    const response = await httpClient.get<{
      data: ListOfDefaultData;
    }>(API_ENDPOINTS.LCMS.COURSES.BUSINESS_UNIT_CREATE_LCMS, {
      params: defualtGetParams
    });
    return response.data;
  },
  getLCMSBusinessUNit: async () => {
    const response = await httpClient.get<{
      data: ListOfDefaultData;
    }>(API_ENDPOINTS.LCMS.COURSES.LCMS_BUSINESS_UNIT, {
      params: defualtGetParams
    });
    return response.data;
  },
  getLCMSJobFamilyGroups: async () => {
    const response = await httpClient.get<{
      data: ListOfJobFamilyGroupData;
    }>(API_ENDPOINTS.LCMS.COURSES.LCMS_JOB_FAMILY_GROUPS, {
      params: defualtGetParams
    });
    return response.data;
  },
  getLCMSJobFamilies: async ({ jobFamilyGroupID }: { jobFamilyGroupID?: string }) => {
    const response = await httpClient.get<{
      data: ListOfJobFamilyData;
    }>(API_ENDPOINTS.LCMS.COURSES.LCMS_JOB_FAMILIES, {
      params: {
        ...defualtGetParams,
        jobFamilyGroupID
      }
    });
    return response.data;
  },
  getLCMSJobPosition: async () => {
    const response = await httpClient.get<{
      data: ListOfJobPositionData;
    }>(API_ENDPOINTS.LCMS.COURSES.LCMS_JOB_POSITION, {
      params: defualtGetParams
    });
    return response.data;
  },
  getLCMSJobProfiles: async (params?: {
    bu?: string;
    jobFamilyId?: string;
    managementLevel?: string;
    pageNumber?: number;
    pageSize?: number;
    search?: string;
  }) => {
    const response = await httpClient.get<{
      pageNumber: number;
      pageSize: number;
      totalPages: number;
      totalRecords: number;
      message: string;
      data: ListOfJobProfileData;
    }>(API_ENDPOINTS.LCMS.COURSES.LCMS_JOB_PROFILES, {
      params: {
        bu: params?.bu || '',
        jobFamilyId: params?.jobFamilyId || '',
        managementLevel: params?.managementLevel || '',
        pageNumber: params?.pageNumber || 1,
        pageSize: params?.pageSize || 10,
        search: params?.search || ''
      }
    });
    return response.data;
  },
  getOverview: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await httpClient.get(API_ENDPOINTS.LCMS.COURSES.OVERVIEW, {
        params
      });
      return response.data;
    } catch {
      return {
        pageNumber: 1,
        pageSize: 10,
        totalPages: 0,
        totalRecords: 0,
        message: 'Failed to fetch overview data',
        data: []
      };
    }
  },
  getGroupTrainingCommandCenter: async (params?: Params) => {
    const response = await httpClient.get<{
      data: ListOfDefaultOptionBinus;
    }>(API_ENDPOINTS.LCMS.COURSES.GROUP_TRAININGS_COMMAND_CENTER, {
      params: {
        ...defualtGetParams,
        ...params
      }
    });
    return response.data;
  }
};
